import { useCallback, useMemo } from 'react';
import type { SortingState, OnChangeFn } from '@tanstack/react-table';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  verticalListSortingStrategy,
  arrayMove,
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { ArrowUpDown, GripVertical, Plus, X, ArrowUp, ArrowDown } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import type { ListViewColumnConfig } from '@/lib/views/listViewSchemas/types';
import { cn } from '@/lib/utils';
import { DEFAULT_ADVANCED_TABLE_LABELS, type AdvancedTableLabels } from './advancedTableLabels';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface SortRule {
  id: string;
  field: string;
  desc: boolean;
}

interface AdvancedSortListProps {
  columns: ListViewColumnConfig[];
  sorting: SortingState;
  onSortingChange: OnChangeFn<SortingState>;
  labels?: Partial<AdvancedTableLabels>;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

let sortIdCounter = 0;
function generateSortId(): string {
  return `s_${Date.now()}_${++sortIdCounter}`;
}

function toSortRules(sorting: SortingState): SortRule[] {
  return sorting.map((s) => ({
    id: generateSortId(),
    field: s.id,
    desc: s.desc,
  }));
}

function fromSortRules(rules: SortRule[]): SortingState {
  return rules
    .filter((r) => r.field)
    .map((r) => ({ id: r.field, desc: r.desc }));
}

// ---------------------------------------------------------------------------
// SortRuleRow (draggable)
// ---------------------------------------------------------------------------

interface SortRuleRowProps {
  rule: SortRule;
  sortableColumns: ListViewColumnConfig[];
  usedFields: Set<string>;
  onChange: (id: string, updates: Partial<SortRule>) => void;
  onRemove: (id: string) => void;
  labels: AdvancedTableLabels;
}

function SortRuleRow({ rule, sortableColumns, usedFields, onChange, onRemove, labels }: SortRuleRowProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: rule.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  const availableColumns = sortableColumns.filter(
    (col) => col.fieldname === rule.field || !usedFields.has(col.fieldname),
  );

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={cn(
        'flex items-center gap-1.5 rounded-sm px-1 py-1',
        isDragging && 'opacity-50',
      )}
    >
      <button
        type="button"
        className="cursor-grab touch-none text-muted-foreground hover:text-foreground"
        aria-label="Reorder sort rule"
        {...attributes}
        {...listeners}
      >
        <GripVertical className="h-3.5 w-3.5" />
      </button>

      <Select
        value={rule.field || undefined}
        onValueChange={(val) => onChange(rule.id, { field: val })}
      >
        <SelectTrigger className="h-7 w-[160px] border border-border/60 rounded-xs px-2 text-xs bg-background">
          <SelectValue placeholder={labels.sortSelectField} />
        </SelectTrigger>
        <SelectContent>
          {availableColumns.map((col) => (
            <SelectItem key={col.fieldname} value={col.fieldname}>
              {col.label ?? col.fieldname}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>

      <Button
        variant="outline"
        size="sm"
        className="h-7 w-7 p-0 shrink-0"
        onClick={() => onChange(rule.id, { desc: !rule.desc })}
        aria-label={rule.desc ? labels.sortDescending : labels.sortAscending}
      >
        {rule.desc ? (
          <ArrowDown className="h-3.5 w-3.5" />
        ) : (
          <ArrowUp className="h-3.5 w-3.5" />
        )}
      </Button>

      <Button
        variant="ghost"
        size="sm"
        className="h-7 w-7 p-0 shrink-0 text-muted-foreground hover:text-destructive"
        onClick={() => onRemove(rule.id)}
        aria-label="Remove sort rule"
      >
        <X className="h-3.5 w-3.5" />
      </Button>
    </div>
  );
}

// ---------------------------------------------------------------------------
// AdvancedSortList
// ---------------------------------------------------------------------------

export function AdvancedSortList({
  columns,
  sorting,
  onSortingChange,
  labels,
}: AdvancedSortListProps) {
  const l = { ...DEFAULT_ADVANCED_TABLE_LABELS, ...labels };

  const sortableColumns = useMemo(
    () => columns.filter((col) => col.sortable !== false),
    [columns],
  );

  const rules = useMemo(() => toSortRules(sorting), [sorting]);

  const usedFields = useMemo(
    () => new Set(rules.map((r) => r.field).filter(Boolean)),
    [rules],
  );

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor),
  );

  const commitRules = useCallback(
    (nextRules: SortRule[]) => {
      onSortingChange(fromSortRules(nextRules));
    },
    [onSortingChange],
  );

  const handleChange = useCallback(
    (id: string, updates: Partial<SortRule>) => {
      const next = rules.map((r) => (r.id === id ? { ...r, ...updates } : r));
      commitRules(next);
    },
    [rules, commitRules],
  );

  const handleRemove = useCallback(
    (id: string) => {
      commitRules(rules.filter((r) => r.id !== id));
    },
    [rules, commitRules],
  );

  const handleAdd = useCallback(() => {
    const newRule: SortRule = {
      id: generateSortId(),
      field: '',
      desc: false,
    };
    commitRules([...rules, newRule]);
  }, [rules, commitRules]);

  const handleClearAll = useCallback(() => {
    onSortingChange([]);
  }, [onSortingChange]);

  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      const { active, over } = event;
      if (!over || active.id === over.id) return;

      const oldIndex = rules.findIndex((r) => r.id === active.id);
      const newIndex = rules.findIndex((r) => r.id === over.id);
      if (oldIndex === -1 || newIndex === -1) return;

      commitRules(arrayMove(rules, oldIndex, newIndex));
    },
    [rules, commitRules],
  );

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          className="h-8 gap-1.5 text-xs"
          aria-label={l.sort}
        >
          <ArrowUpDown className="h-3.5 w-3.5" />
          {l.sort}
          {sorting.length > 0 && (
            <Badge
              variant="secondary"
              className="ml-0.5 h-5 min-w-[20px] rounded-full px-1.5 text-[10px] font-semibold"
            >
              {sorting.length}
            </Badge>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[320px] p-2" align="start">
        <div className="flex flex-col gap-1">
          <p className="px-1 text-xs font-medium text-muted-foreground">
            {l.sortRules}
          </p>

          {rules.length > 0 ? (
            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragEnd={handleDragEnd}
            >
              <SortableContext
                items={rules.map((r) => r.id)}
                strategy={verticalListSortingStrategy}
              >
                {rules.map((rule) => (
                  <SortRuleRow
                    key={rule.id}
                    rule={rule}
                    sortableColumns={sortableColumns}
                    usedFields={usedFields}
                    onChange={handleChange}
                    onRemove={handleRemove}
                    labels={l}
                  />
                ))}
              </SortableContext>
            </DndContext>
          ) : (
            <p className="px-1 py-2 text-xs text-muted-foreground">
              {l.sortNoRules}
            </p>
          )}

          <div className="flex items-center justify-between px-1 pt-1">
            <Button
              variant="ghost"
              size="sm"
              className="h-7 gap-1 text-xs"
              onClick={handleAdd}
              disabled={usedFields.size >= sortableColumns.length}
            >
              <Plus className="h-3 w-3" />
              {l.sortAdd}
            </Button>
            {rules.length > 0 && (
              <Button
                variant="ghost"
                size="sm"
                className="h-7 text-xs text-muted-foreground hover:text-destructive"
                onClick={handleClearAll}
              >
                {l.sortClearAll}
              </Button>
            )}
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}
